import React from "react"
import HorizontalField, {optionalHorizontalField} from "../HorizontalField/HorizontalField"
import {UnitOrBankUserOnly} from "../../containers/PermissionedUser/PermissionedUser"
import {OrgName} from "../OrgName/OrgName"
import {Accordion} from "../Accordion/Accordion"
import {CountriesOfOperation} from "../ApplicationAdditionalInformationFields/CountriesOfOperation"
import {InformationHiding, ssnPreview, phonePreview} from "../InformationHiding/InformationHiding"
import {addressText, booleanToYesNo} from "../../utilities/helpers"
import {Resource} from "../../resources/common"
import {BusinessProductApplication} from "../../resources/productApplication"

interface BusinessInformationProductApplicationProps {
	business: BusinessProductApplication
	defaultInformationHidingState: boolean
	included?: Resource[]
}

export default function InformationBusinessProductApplication({
	business,
	defaultInformationHidingState,
	included,
}: BusinessInformationProductApplicationProps) {
	const {attributes} = business

	return (
		<div className="card">
			<div className="card-header">
				<p className="card-header-title">Business Information</p>
			</div>
			<div className="card-content">
				<UnitOrBankUserOnly>
					<HorizontalField label="Org">
						<OrgName orgId={business.relationships.org.data.id} included={included} element={"span"} />
					</HorizontalField>
				</UnitOrBankUserOnly>
				<HorizontalField label="Application Id">
					<input type="text" className="input is-static" readOnly value={business.id} />
				</HorizontalField>
				{business.relationships.customer ? (
					<HorizontalField label="Customer Id">
						<input type="text" readOnly value={business.relationships.customer.data.id} className="input is-static" />
					</HorizontalField>
				) : undefined}
				<HorizontalField label="Name">
					<input type="text" readOnly value={attributes.name} className="input is-static" />
				</HorizontalField>
				{optionalHorizontalField("Doing Business As", attributes.dba)}
				{optionalHorizontalField("Entity Type", attributes.entityType)}
				{attributes.ein ? (
					<HorizontalField label="EIN">
						<InformationHiding
							element={(value) => <input type="text" readOnly value={value} className="input is-static" />}
							getValue={() => attributes.ein || ""}
							placeholder={ssnPreview(attributes.ein)}
							defaultIsHidden={defaultInformationHidingState}
							isHiddenIconVisible={true}
						/>
					</HorizontalField>
				) : undefined}
				{optionalHorizontalField("Incorporation State", attributes.stateOfIncorporation)}
				{attributes.phone ? (
					<HorizontalField label="Phone">
						<InformationHiding
							element={(value) => <input type="text" readOnly value={value} className="input is-static" />}
							getValue={() => `+${attributes.phone?.countryCode} ${attributes.phone?.number}`}
							placeholder={phonePreview(`+${attributes.phone?.countryCode} ${attributes.phone?.number}`)}
							defaultIsHidden={defaultInformationHidingState}
							isHiddenIconVisible={true}
						/>
					</HorizontalField>
				) : undefined}
				{optionalHorizontalField("Website", attributes.website)}
				{attributes.address && (
					<HorizontalField label="Address">
						<textarea
							readOnly
							value={addressText(attributes.address)}
							rows={attributes.address.street2 ? 4 : 3}
							className="textarea is-static has-fixed-size"
						/>
					</HorizontalField>
				)}
				{attributes.operatingAddress && (
					<HorizontalField label="Operating Address">
						<textarea
							readOnly
							value={addressText(attributes.operatingAddress)}
							rows={attributes.operatingAddress.street2 ? 4 : 3}
							className="textarea is-static has-fixed-size"
						/>
					</HorizontalField>
				)}
				{optionalHorizontalField(
					"Attested no beneficial owners",
					booleanToYesNo(attributes.attestedNoBeneficialOwners)
				)}
				<Accordion title={"Additional Information"} theme="light" noContentPadding>
					{optionalHorizontalField("Industry", attributes.industry)}
					{optionalHorizontalField("Stock Symbol", attributes.stockSymbol)}
					{optionalHorizontalField("Business Vertical", attributes.businessVertical)}
					{optionalHorizontalField("Year of Incorporation", attributes.yearOfIncorporation)}
					{optionalHorizontalField("Annual Revenue", attributes.annualRevenue)}
					{optionalHorizontalField("Cash Flow", attributes.cashFlow)}
					<CountriesOfOperation countriesOfOperation={attributes.countriesOfOperation} isArchived={false} />
					{optionalHorizontalField("Number of Employees", attributes.numberOfEmployees)}
					{optionalHorizontalField("Business Industry", attributes.businessIndustry)}
					{optionalHorizontalField("Source of Funds", attributes.sourceOfFunds)}
					{optionalHorizontalField("Source of Funds Description", attributes.sourceOfFundsDescription)}
					{optionalHorizontalField("Account Purpose", attributes.accountPurpose)}
					{optionalHorizontalField("Account Purpose Detail", attributes.accountPurposeDetail)}
					{optionalHorizontalField("Transaction Volume", attributes.transactionVolume)}
					{optionalHorizontalField("Transaction Volume Description", attributes.transactionVolumeDescription)}
					{optionalHorizontalField("Is Regulated", booleanToYesNo(attributes.isRegulated))}
					{optionalHorizontalField("Regulator Name", attributes.regulatorName)}
					{attributes.usNexus && optionalHorizontalField("US Nexus", attributes.usNexus.join(", "))}
					{optionalHorizontalField("Business Description", attributes.businessDescription)}
				</Accordion>
			</div>
		</div>
	)
}
